// Global hover tooltip for `[data-tip]` elements.
//
// The old pure-CSS ::after tooltip was a child of its trigger, so it was
// clipped by any ancestor `overflow` and trapped under sibling stacking
// contexts, and it always rendered on one fixed side of the trigger. This
// renders a single body-mounted element instead (above every app layer) and
// flips placement when the preferred side would leave the viewport.
//
// API (unchanged from the CSS version):
//   data-tip="text"        — tooltip text
//   data-tip-pos="right"   — prefer the right side (default: above)
// Opt out for a subtree with `--tip: off` in CSS.

let tipEl: HTMLDivElement | null = null
let current: Element | null = null

function ensureEl(): HTMLDivElement {
  if (!tipEl) {
    tipEl = document.createElement('div')
    tipEl.className = 'tip-pop'
    tipEl.setAttribute('role', 'tooltip')
    document.body.appendChild(tipEl)
  }
  return tipEl
}

function hide() {
  current = null
  tipEl?.classList.remove('in')
}

function show(trigger: Element) {
  const text = trigger.getAttribute('data-tip')
  if (!text) return
  if (window.matchMedia('(pointer: coarse)').matches) return
  if (getComputedStyle(trigger).getPropertyValue('--tip').trim() === 'off') return

  current = trigger
  const el = ensureEl()
  el.classList.remove('in')
  el.textContent = text

  const r = trigger.getBoundingClientRect()
  const tw = el.offsetWidth
  const th = el.offsetHeight
  const gap = 8   // distance from the trigger
  const pad = 8   // minimum distance from the viewport edge
  const vw = window.innerWidth
  const vh = window.innerHeight

  let left: number
  let top: number
  if (trigger.getAttribute('data-tip-pos') === 'right') {
    left = r.right + gap
    if (left + tw > vw - pad) left = r.left - tw - gap // flip to the left side
    top = r.top + r.height / 2 - th / 2
  } else {
    top = r.top - th - gap
    if (top < pad) top = r.bottom + gap // flip below
    left = r.left + r.width / 2 - tw / 2
  }
  el.style.left = `${Math.round(Math.max(pad, Math.min(vw - tw - pad, left)))}px`
  el.style.top = `${Math.round(Math.max(pad, Math.min(vh - th - pad, top)))}px`
  requestAnimationFrame(() => {
    if (current === trigger) el.classList.add('in')
  })
}

export function installTooltips() {
  document.addEventListener('mouseover', (e) => {
    const t = (e.target as Element | null)?.closest?.('[data-tip]') ?? null
    if (t === current) return
    if (t) show(t)
    else hide()
  })
  // The trigger is usually about to move, disappear, or get covered.
  document.addEventListener('mousedown', hide, true)
  document.addEventListener('scroll', hide, true)
  document.addEventListener('mouseleave', hide)
  window.addEventListener('blur', hide)
  window.addEventListener('resize', hide)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide()
  })
}
